import { isTimeoutError } from './timeout-signal';

/** Exponential backoff schedule for resilientRetry callables: 1s, 2s, 4s, 8s, 15s = 30s total wait.
 * With the post-#3570 80s per-attempt timeout, the full 5-retry schedule yields a 510s ≈ 8.5 min
 * worst-case wall-clock when the network layer is fully hung. Callables that gate user-blocking
 * UI (loaders, modals) opt into `maxRetries: 3` via `callable()` to cap at ~5.5 min instead — see
 * #3575 and the per-callable rationale at the call-site declarations in cloud-functions.service.ts.
 *
 * Lives in a separate Angular-free module so the test file can import it without dragging in
 * `@angular/common/http` and the full DI graph (see cloud-functions.service.test.ts). */
export const RESILIENT_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000] as const;

/** Returns the backoff schedule, optionally capped to the first `maxRetries` delays.
 * `maxRetries === undefined` yields the full schedule (5 retries, the historical default). */
export function getRetryDelays(maxRetries?: number): readonly number[] {
  if (maxRetries === undefined) return RESILIENT_RETRY_DELAYS_MS;
  return RESILIENT_RETRY_DELAYS_MS.slice(0, Math.max(0, maxRetries));
}

/** Returns true for HTTP errors that the resilient-retry layer should treat as
 *  retriable: 502 Bad Gateway and 503 Service Unavailable.
 *
 *  Motivation (Bug 11, Sentry CYBERNETIC-FITNESS-APP-44E): a transient Cloud
 *  Run scale-up race surfaces as a single 503 to the client. Without retry,
 *  the user sees a hard failure on a request that would succeed on a second
 *  attempt seconds later.
 *
 *  Scope: 502 and 503 only — these are the two statuses where a same-request
 *  retry is overwhelmingly likely to recover. 500 is treated as terminal
 *  (application-level error, retry won't help). 504 Gateway Timeout is also
 *  excluded: for the default 80s per-attempt timeout our `createTimeoutSignal`
 *  typically aborts the request locally before the gateway returns 504, so
 *  the failure surfaces as a TimeoutError that the wrapper already handles.
 *  Long-budget callables (e.g. `solveWorkoutsAsync` at timeoutMs: 200_000)
 *  could see a true gateway 504 first, but those are also state-sensitive
 *  one-shot operations where a retry would burn another full timeout and
 *  rarely change the outcome — explicitly out of scope for this predicate.
 *
 *  Duck-typing on `httpStatus` keeps this helper Angular-free (mirrors the
 *  module's broader constraint — the cloud-functions wrapper attaches
 *  `httpStatus` to its `CloudFunctionHttpError`, but this helper does not
 *  import the class to avoid a cycle). */
export function isRetriableHttpError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const status = (error as { httpStatus?: unknown }).httpStatus;
  return status === 502 || status === 503;
}

/** What a callable reports when its last attempt failed on the network (not with an HTTP status).
 *  Both reporting values are filed only after the online check says the device is online: the
 *  check pings the app's own address with a short deadline of its own, so it cannot hang on an
 *  API server that is not answering. */
export enum TerminalNetworkReport {
  /** the caller declared `silentNetworkErrors`: it owns the failure (couchLogin's caller reports
   *  it; the telemetry flusher backs off and deliberately reports nothing) */
  None = 'None',
  /** the last attempt ran out of time: filed as `Cloud function "<name>" timed out` */
  ReportTimeout = 'ReportTimeout',
  /** any other network failure, with the app's own connection state Online through every attempt:
   *  filed as `Cloud function "<name>" network error while online` */
  ReportNetworkError = 'ReportNetworkError',
  /** a non-timeout network failure while the app's own connection state read Offline at some point
   *  during the attempts: expected, so only logged (#6609, an Android resume that called the server
   *  before the phone could look up the host again) */
  LogOfflineDuringAttempts = 'LogOfflineDuringAttempts',
  /** a non-timeout network failure while the page was hidden at some point during the attempts,
   *  with the connection state Online throughout: expected, so only logged (df-50916fe9c89e, Android
   *  cut a frozen background app's network while the app's own state stayed Online) */
  LogHiddenDuringAttempts = 'LogHiddenDuringAttempts',
}

/** Decides the terminal report for a callable whose retries ran out on a network failure.
 *  Since #6058 the global fetch wrapper no longer reports these requests, so this is the only
 *  report a failed cloud-function call gets -- a timeout included.
 *
 *  `wasOfflineDuringAttempts` is true when the app's connection state read Offline before or after
 *  any attempt. The online check that runs after the last attempt answers only for that later
 *  moment: on a resume it can already find online while every attempt failed during the reconnect,
 *  which is what filed the false "while online" reports in #6609. Timeouts keep their report.
 *
 *  `wasHiddenDuringAttempts` is true when the page was hidden at any point during the call. Android
 *  can cut a background app's network while the app's own state stays Online, so the offline check
 *  above misses it (df-50916fe9c89e). A failure the user never saw in the foreground is only logged. */
export function decideTerminalNetworkReport(lastError: unknown, silentNetworkErrors: boolean, wasOfflineDuringAttempts: boolean, wasHiddenDuringAttempts: boolean): TerminalNetworkReport {
  if (silentNetworkErrors) return TerminalNetworkReport.None;
  if (isTimeoutError(lastError)) return TerminalNetworkReport.ReportTimeout;
  if (wasOfflineDuringAttempts) return TerminalNetworkReport.LogOfflineDuringAttempts;
  if (wasHiddenDuringAttempts) return TerminalNetworkReport.LogHiddenDuringAttempts;
  return TerminalNetworkReport.ReportNetworkError;
}

/** The two fetch implementations `pickApiFetchImpl` chooses between. On a
 *  Capacitor native platform the bridge saves the original WebKit fetch as
 *  `window.CapacitorWebFetch` before it replaces `window.fetch` with the
 *  native-HTTP patch; on web the saved copy does not exist. */
export interface ApiFetchWindow {
  fetch: typeof fetch;
  CapacitorWebFetch?: typeof fetch;
}

/** Chooses the fetch implementation for a cloud-function call (#4038).
 *
 *  Background: with the CapacitorHttp plugin enabled, `window.fetch` is
 *  patched to run requests through the native HTTP layer. That patch ignores
 *  the JS `AbortSignal`, and on iOS the native task runs on `URLSession.shared`,
 *  whose session-level 60s timeout is more restrictive than the 600s
 *  request-level value the plugin sets — Apple applies the more restrictive
 *  policy, so EVERY patched request on iOS dies after ~60s. That killed
 *  legitimately slow (~85s) max-effort solveWorkouts runs: the server finished
 *  with 200 OK, but the phone had already given up (#4038).
 *
 *  Callables that must outlive that cap opt in via `webFetchOnNative` and get
 *  the browser's original fetch on native platforms instead: it honors the
 *  caller's AbortSignal timeout (restoring the #3366/#3570 client/server
 *  timeout pairing) and throws standard TypeErrors that `isNetworkError`
 *  already classifies. The server's CORS explicitly allow-lists the native
 *  WebView origins (`capacitor://localhost`, `https://localhost`), and /api
 *  auth is Bearer-token, so no native cookie handling is needed.
 *
 *  Falls back to the patched `window.fetch` when the saved original is absent
 *  (web/PWA, or a future bridge change). Both returns are bound to `win`
 *  because a detached `fetch` throws "Illegal invocation" in some engines.
 *  Angular-free and window-injected so the unit test can exercise it. */
export function pickApiFetchImpl(webFetchOnNative: boolean, isNative: boolean, win: ApiFetchWindow): typeof fetch {
  // if this callable opted in and the bridge saved the browser original, use it
  if (webFetchOnNative && isNative && typeof win.CapacitorWebFetch === 'function') {
    return win.CapacitorWebFetch.bind(win);
  }
  return win.fetch.bind(win);
}

/** Returns true for errors the cloud-functions wrapper has already
 *  retried and reported to Sentry on its own — either a
 *  `CloudFunctionNetworkError` (network-layer exhaustion) or a
 *  `CloudFunctionHttpError` with a retriable status (502/503).
 *
 *  Callers' catch handlers should branch on this predicate before
 *  reaching for `LoggingService.trackAbnormalException`: the
 *  trackAbnormalException dedup in logging.service.ts swallows these
 *  exact classes (the wrapper already reported), so a UX layer that
 *  defers to trackAbnormalException for them would leave the user
 *  with no feedback at all. Use this predicate to route those failures
 *  through a Retry / Contact-Support alert instead, then fall back to
 *  trackAbnormalException only for genuinely unexpected errors.
 *
 *  Duck-typed on `.name` and `.httpStatus` so callers don't need to
 *  import the class types and the predicate stays trivially testable
 *  in the Angular-free retry module. */
export function isTransportFailureAfterRetries(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const name = (error as { name?: unknown }).name;
  if (name === 'CloudFunctionNetworkError') return true;
  if (name === 'CloudFunctionHttpError') return isRetriableHttpError(error);
  return false;
}
